/**
 * Oct 20 founder addendum (L6) — "Vehicle returned": the front office receives a vehicle that left "Picked Up — Work
 * Incomplete". Customer → FOH → BOH: the vehicle lands with the person checking it in (front-office custody), at the
 * place they say it is (parking spot, general lot, or on site — never straight into a bay). No timer restarts, no bay
 * is occupied, no technician time is reserved; old released days are not re-booked. The server
 * (check_in_returned_vehicle) decides and records; this only asks where the vehicle is.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { readParkingSpotChoices, type ParkingSpotChoice } from '@/lib/location/api';
import { checkInReturnedVehicle, type ActAnswer, type ReturnLocation } from '@/lib/foh/pickup/departureActs';

export interface ReturnCheckInDialogProps {
  open: boolean;
  ticketId: string | null;
  shopId: string;
  subject: string | null;
  onClose: () => void;
  onDone: (answer: ActAnswer) => void;
}

type Choice = { key: string; label: string; where: ReturnLocation };

export function ReturnCheckInDialog({ open, ticketId, shopId, subject, onClose, onDone }: ReturnCheckInDialogProps) {
  const [spots, setSpots] = useState<ParkingSpotChoice[]>([]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !ticketId) return;
    setChosen(null); setNote(''); setRefusal(null);
    let live = true;
    readParkingSpotChoices(shopId, ticketId).then((s) => { if (live) setSpots(s); }).catch(() => { if (live) setSpots([]); });
    return () => { live = false; };
  }, [open, ticketId, shopId]);

  const choices: Choice[] = [
    ...spots.filter((s) => !s.occupied).map((s) => ({ key: `spot:${s.id}`, label: `Spot ${s.label}`, where: { kind: 'PARKING_SPOT', parkingSpotId: s.id } as ReturnLocation })),
    { key: 'general', label: 'General lot', where: { kind: 'ON_SITE_GENERAL' } },
    { key: 'unknown', label: 'On site — exact place unknown', where: { kind: 'ON_SITE_UNKNOWN_EXACT' } },
  ];
  const pick = choices.find((c) => c.key === chosen) ?? null;

  const submit = async () => {
    if (!ticketId || !pick || busy) return;
    setBusy(true);
    try {
      const a = await checkInReturnedVehicle(ticketId, pick.where, note.trim() || null);
      if (!a.ok) { setRefusal(a.message); return; }
      onDone(a);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="return-check-in-dialog">
        <DialogHeader>
          <DialogTitle>Vehicle returned</DialogTitle>
          <DialogDescription>
            {subject ? `${subject} is back. ` : 'The vehicle is back. '}
            It is received by the front office — you hold it until the shop is ready. Nothing is scheduled, no timer
            restarts and no bay is taken.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-sm font-medium">Where is it now?</Label>
          <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Where the vehicle is">
            {choices.map((c) => (
              <button key={c.key} type="button" role="radio" aria-checked={chosen === c.key} disabled={busy}
                      data-testid={`return-check-in-${c.key}`} onClick={() => setChosen(c.key)}
                      className={cn('min-h-11 rounded-md border px-3 py-2 text-left text-sm',
                        chosen === c.key ? 'border-primary bg-primary/10' : 'hover:bg-muted')}>
                {c.label}
              </button>
            ))}
          </div>
          <Label htmlFor="return-check-in-note" className="text-sm">Note (optional)</Label>
          <Textarea id="return-check-in-note" rows={2} maxLength={500} value={note} disabled={busy}
                    onChange={(e) => setNote(e.target.value)} />
          {refusal && <p className="text-sm text-destructive" data-testid="return-check-in-refusal">{refusal}</p>}
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" className="min-h-11" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="min-h-11" disabled={!pick || busy} onClick={() => { void submit(); }} data-testid="return-check-in-confirm">
            {busy ? 'Checking in…' : 'Check vehicle in'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
